import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RefreshActions } from '../../state/refresh'
import { useRequests } from '../../requests/useRequests'
import { Product } from '../../types/product'
import { State } from '../../types/state'
import { PageBanner } from '../Banners/PageBanner'
import Header from '../Header/Header'
import Footer from '../Footer/Footer'

const ProductDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const requests = useRequests()

  // Poprawiony selektor dla roli użytkownika
  const user = useSelector((state: any) => state.user)
  const { username } = useSelector((state: State) => state.user)
  const isSuperAdmin = user && user.isSuperadmin === true

  const isAddMode = new URLSearchParams(location.search).get('add') === 'true'

  const productId = parseInt(id || '0', 10)

  const [product, setProduct] = useState<Product | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [fileUrls, setFileUrls] = useState<Record<string, string>>({})

  // Funkcja do wyświetlania powiadomień
  const showToast = (message: string) => {
    dispatch(
      RefreshActions.setToastMessage({
        message
      })
    )
  }

  // Pobieranie danych produktu i URL-i dla plików
  useEffect(() => {
    if (isNaN(productId) || productId <= 0) {
      showToast('Nieprawidłowy identyfikator produktu')
      navigate('/products')
      return
    }

    setLoading(true)
    requests.getProduct(productId)
      .then(response => {
        setProduct(response.data)

        // Jeśli produkt ma pliki, pobierz dla nich podpisane URL-e
        if (response.data.files && response.data.files.length > 0) {
          const fileUrlPromises = response.data.files.map(async (fileKey: string) =>
            await requests.getFileUrl(fileKey)
              .then(res => ({ fileKey, url: res.data.presignedUrl }))
              .catch(err => {
                console.error(`Błąd pobierania URL dla pliku ${fileKey}:`, err)
                return { fileKey, url: '' }
              })
          )

          Promise.all(fileUrlPromises)
            .then(results => {
              const urlsMap = results.reduce<Record<string, string>>((acc, { fileKey, url }) => {
                acc[fileKey] = url
                return acc
              }, {})

              setFileUrls(urlsMap)
            })
        }

        setLoading(false)
      })
      .catch(error => {
        console.error('Błąd podczas pobierania danych produktu:', error)
        showToast('Nie udało się pobrać danych produktu')
        setLoading(false)
        navigate('/products')
      })
  }, [productId])

  // Funkcja do obsługi przycisku edycji
  const handleEditProduct = () => {
    navigate(`/products/${id}/edit`)
  }

  // Funkcja do obsługi przycisku usunięcia
  const handleDeleteProduct = async () => {
    if (product == null) return

    if (window.confirm('Czy na pewno chcesz usunąć ten produkt?')) {
      try {
        await requests.deleteProduct(product.id)
        showToast('Produkt został usunięty')
        navigate('/products')
      } catch (error) {
        console.error('Błąd podczas usuwania produktu:', error)
        showToast('Wystąpił błąd podczas usuwania produktu')
      }
    }
  }

  // Funkcja do renderowania hierarchicznej nazwy kategorii
  const renderCategoryPath = (category: string) => {
    if (!category) return null

    const parts = category.split('/')
    if (parts.length <= 1) {
      return category
    }

    return (
      <div className='category-path'>
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className='category-separator'> / </span>}
            <span className={`category-level-${index}`}>
              {part}
            </span>
          </React.Fragment>
        ))}
      </div>
    )
  }

  // Nawigacja do marki
  const handleBrandClick = () => {
    if (product != null) {
      navigate(`/brands/${product.brandId}`)
    }
  }

  // Nawigacja do listy produktów z filtrem na format pliku
  const handleFormatClick = (format: string) => {
    navigate(`/products?format=${encodeURIComponent(format)}`)
  }

  // Funkcja do pobrania nazwy pliku z ścieżki
  const getFileName = (filePath: string) => {
    return filePath.split('/').pop() || filePath
  }

  // Funkcja do określenia ikony dla pliku na podstawie rozszerzenia
  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase()

    switch (extension) {
      case '3ds': return 'fas fa-cube'
      case 'gsm': return 'fas fa-building'
      case 'dwg':
      case 'dxf': return 'fas fa-drafting-compass'
      case 'obj':
      case 'mtl': return 'fas fa-cube'
      case 'skp': return 'fas fa-cube'
      case 'aco': return 'fas fa-file'
      case 'dae': return 'fas fa-cube'
      case 'fbx': return 'fas fa-cube'
      case 'iwo': return 'fas fa-cube'
      case '3dm': return 'fas fa-cube'
      case 'wrl': return 'fas fa-cube'
      case 'stl': return 'fas fa-cube'
      case 'jpg':
      case 'jpeg':
      case 'png': return 'fas fa-file-image'
      default: return 'fas fa-file'
    }
  }

  // Funkcja do grupowania plików po rozszerzeniach
  const groupFilesByExtension = (files: string[]) => {
    return files.reduce((groups: Record<string, string[]>, file) => {
      const extension = file.split('.').pop()?.toLowerCase() || 'other'
      if (!groups[extension]) {
        groups[extension] = []
      }
      groups[extension].push(file)
      return groups
    }, {})
  }

  // Funkcja do pobierania podpisanego URL dla pliku
  const handleFileDownload = async (fileKey: string) => {
    try {
      // Najpierw śledzimy pobranie
      await requests.trackProductDownload(productId, fileKey)

      // Następnie pobieramy URL
      const url = fileUrls[fileKey]
      if (url) {
        window.open(url, '_blank')
      } else {
        // Jeśli nie mamy URL w stanie, spróbuj pobrać go bezpośrednio
        const response = await requests.getFileUrl(fileKey)
        if (response.data.presignedUrl) {
          window.open(response.data.presignedUrl, '_blank')
        } else {
          showToast('Nie można pobrać pliku')
        }
      }
    } catch (error) {
      console.error('Błąd podczas pobierania pliku:', error)
      showToast('Wystąpił problem z pobieraniem pliku')
    }
  }

  if (loading) {
    return (
      <div className='container my-5'>
        <div className='text-center py-5'>
          <div className='spinner-border text-primary' role='status'>
            <span className='sr-only'>Ładowanie...</span>
          </div>
        </div>
      </div>
    )
  }

  if (product == null) {
    return (
      <div className='container my-5'>
        <div className='text-center py-5'>
          <h3>Nie znaleziono produktu</h3>
          <button
            className='btn btn__primary mt-3'
            onClick={() => navigate('/products')}
          >
            Wróć do listy produktów
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Header />
      <PageBanner title={product.name} description={`Produkt marki ${product.brand.name}`} />

      <section className='product-details tw-py-24 md:tw-py-28 lg:tw-py-36'>
        <div className='container'>
          {/* Przyciski administracyjne na górze strony dla lepszej widoczności */}
          {isSuperAdmin && (
            <div className='row mb-4'>
              <div className='col-12'>
                <div className='admin-actions d-flex justify-content-end'>
                  <button
                    className={`
                                      tw-mr-2 tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                                    `}
                    onClick={handleEditProduct}
                  >
                    <i className='fas fa-edit mr-1' /> Edytuj produkt
                  </button>

                  <button
                    className={`
                                      tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                                      tw-text-white
                                      tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-red-500 hover:tw-bg-opacity-90 focus:tw-bg-red-500
                                    `}
                    onClick={handleDeleteProduct}
                  >
                    <i className='fas fa-trash-alt mr-1' /> Usuń produkt
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className='row'>
            {/* Zdjęcie produktu */}
            <div className='col-lg-6 mb-4 mb-lg-0'>
              <div className='product-image-container'>
                <img
                  src={product.imagePresignedUrl || product.image}
                  alt={product.name}
                  className='img-fluid rounded'
                />
              </div>
            </div>

            {/* Informacje o produkcie */}
            <div className='col-lg-6'>
              <div className='product-info'>
                <h2 className='product-title mb-3'>{product.name}</h2>

                <div className='product-meta mb-4'>
                  <div className='product-brand mb-2'>
                    <span className='meta-label'>Marka:</span>
                    <button
                      className='tw-ml-4 tw-font-semibold tw-text-blue-500 hover:tw-text-blue-700 hover:tw-underline'
                      onClick={handleBrandClick}
                    >
                      {product.brand.name}
                    </button>
                  </div>

                  {product.code && (
                    <div className='product-code mb-2'>
                      <span className='meta-label'>Kod produktu:</span>
                      <span className='meta-value ml-2'>{product.code}</span>
                    </div>
                  )}

                  <div className='product-category mb-2'>
                    <span className='meta-label'>Kategoria:</span>
                    <span className='meta-value ml-2'>
                      {renderCategoryPath(product.category)}
                    </span>
                  </div>
                </div>

                {product.url && <><div className='product-description mb-4'>
                  <a
                    href={product.url} target='_blank' rel='noopener noreferrer' className={`
                    tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                    tw-text-white
                    tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                  `}
                  >
                    <i className='fas fa-external-link-alt mr-2' />
                    Odwiedź stronę produktu
                  </a>
                </div>
                </>}

                {!isAddMode && <><div className='product-description mb-4'>
                  <h3 className='section-title tw-text-lg tw-font-bold tw-mb-3'>Opis</h3>
                  <br />
                  <p>{product.description || 'Brak opisu produktu.'}</p>
                </div>

                  <div className='product-formats mb-4'>
                    <h3 className='section-title tw-text-lg tw-font-bold tw-mb-3'>Dostępne formaty plików</h3>
                    <br />
                    <div className='format-badges'>
                      {product.fileFormats.map((format, index) => (
                        <span
                          key={index}
                          className='badge badge-secondary mr-2 mb-2 p-2'
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleFormatClick(format)}
                        >
                          {format}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Nowa sekcja z plikami do pobrania */}
                  {(product.files != null) && product.files.length > 0 && (
                    <div className='product-files mb-4'>
                      <h3 className='section-title tw-text-lg tw-font-bold tw-mb-3'>Pliki do pobrania</h3>
                      <br />
                      {username
                        ? (
                          <div className='list-group'>
                            {Object.entries(groupFilesByExtension(product.files)).map(([extension, files]) => (
                              <div key={extension} className='file-group mb-3'>
                                <h5 className='file-group-title mb-2'>
                                  <i className={`${getFileIcon(`.${extension}`)} mr-2`} />
                                  {extension.toUpperCase()}
                                </h5>
                                {files.map((file, index) => (
                                  <button
                                    key={index}
                                    className='list-group-item list-group-item-action d-flex align-items-center'
                                    onClick={async () => await handleFileDownload(file)}
                                  >
                                    <i className={`${getFileIcon(file)} mr-3`} />
                                    <span>{getFileName(file)}</span>
                                  </button>
                                ))}
                              </div>
                            ))}
                          </div>
                          )
                        : (
                          <div className='alert alert-info'>
                            <i className='fas fa-info-circle mr-2' />
                            Aby pobrać pliki, musisz się zalogować.
                          </div>
                          )}
                    </div>
                  )}

                  <div className='product-actions mt-5'>
                    <button
                      className={`
                        tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors
                        tw-text-white
                        tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base tw-bg-green hover:tw-bg-opacity-90 focus:tw-bg-green
                      `}
                      onClick={() => navigate('/products')}
                    >
                      <i className='fas fa-arrow-left mr-2' />
                      Powrót do listy
                    </button>
                  </div>
                </>}

              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}

export default ProductDetails
